import React, { useEffect, useMemo, useState } from 'react';
import { Image as ImageIcon, Upload, Trash2, Eye, Edit, X } from 'lucide-react';
import { fetchBanners, createBanner, deleteBanner, updateBanner, fetchCategories } from '../api';
import { getImageUrl } from '../api/imageUtils';

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploadType, setUploadType] = useState('home'); // home | who | category
  const [title, setTitle] = useState('home');
  const [categoryName, setCategoryName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editFile, setEditFile] = useState(null);
  const [editSection, setEditSection] = useState('banner');
  const [editCategoryName, setEditCategoryName] = useState('');
  const [viewFilter, setViewFilter] = useState('home'); // home | who | category | all

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [bannerList, categoryList, cats] = await Promise.all([
          fetchBanners('banner').catch(() => []),
          fetchBanners('category').catch(() => []),
          fetchCategories().catch(() => []),
        ]);
        if (!cancelled) {
          setBanners([...(Array.isArray(bannerList) ? bannerList : []), ...(Array.isArray(categoryList) ? categoryList : [])]);
          setCategories(Array.isArray(cats) ? cats : []);
        }
      } catch (e) {
        console.error('Failed to load banners', e);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFileChange = (e) => {
    const incoming = Array.from(e.target.files || []);
    if (!incoming.length) return;
    setFiles((prev) => [...prev, ...incoming]);
    e.target.value = '';
  };

  const removeSelected = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const computedSection = uploadType === 'category' ? 'category' : 'banner';
  const computedTitle = uploadType === 'home' ? 'home' : uploadType === 'who' ? 'who-we-are' : title;

  const handleSave = async () => {
    setError('');
    if (!files.length) {
      setError('Please select at least one image to upload.');
      return;
    }
    if (uploadType === 'category' && !categoryName) {
      setError('Please select a category for category images.');
      return;
    }
    setSaving(true);
    try {
      for (const file of files) {
        await createBanner({
          file,
          title: computedTitle,
          section: computedSection,
          categoryName: uploadType === 'category' ? categoryName : '',
        });
      }
      setFiles([]);
      setCategoryName('');
      setTitle('home');
      const [bannerList, categoryList] = await Promise.all([
        fetchBanners('banner').catch(() => []),
        fetchBanners('category').catch(() => []),
      ]);
      setBanners([...(Array.isArray(bannerList) ? bannerList : []), ...(Array.isArray(categoryList) ? categoryList : [])]);
    } catch (e) {
      console.error('Failed to upload banners', e);
      setError(e.response?.data?.error || e.message || 'Failed to upload banners');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner image?')) return;
    try {
      await deleteBanner(id);
      setBanners((prev) => prev.filter((b) => b.id !== id));
    } catch (e) {
      console.error('Failed to delete banner', e);
      alert(e.response?.data?.error || e.message || 'Failed to delete banner');
    }
  };

  const openEdit = (banner) => {
    setEditing(banner);
    setEditTitle(banner.title || '');
    setEditIsActive(Boolean(banner.isActive));
    setEditFile(null);
    setEditSection(banner.section || 'banner');
    setEditCategoryName(banner.categoryName || '');
  };

  const closeEdit = () => {
    setEditing(null);
    setEditTitle('');
    setEditIsActive(true);
    setEditFile(null);
    setEditSection('banner');
    setEditCategoryName('');
  };

  const saveEdit = async () => {
    if (!editing?.id) return;
    setSaving(true);
    try {
      await updateBanner(editing.id, {
        title: editTitle,
        section: editSection,
        categoryName: editSection === 'category' ? editCategoryName : '',
        isActive: editIsActive,
        file: editFile || undefined,
      });
      const [bannerList, categoryList] = await Promise.all([
        fetchBanners('banner').catch(() => []),
        fetchBanners('category').catch(() => []),
      ]);
      setBanners([...(Array.isArray(bannerList) ? bannerList : []), ...(Array.isArray(categoryList) ? categoryList : [])]);
      closeEdit();
    } catch (e) {
      console.error('Failed to update banner', e);
      alert(e.response?.data?.error || e.message || 'Failed to update banner');
    } finally {
      setSaving(false);
    }
  };

  const filteredBanners = useMemo(() => {
    if (viewFilter === 'all') return banners;
    if (viewFilter === 'category') return banners.filter((b) => b.section === 'category');
    if (viewFilter === 'home') return banners.filter((b) => b.section === 'banner' && (b.title || '').toLowerCase() === 'home');
    if (viewFilter === 'who') return banners.filter((b) => b.section === 'banner' && (b.title || '').toLowerCase() === 'who-we-are');
    return banners;
  }, [banners, viewFilter]);

  return (
    <div className="ui-page animate-fade-in">
      {/* HEADER SECTION */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight text-gradient">Banner Assets Manager</h1>
          <p className="text-slate-500 mt-1 font-medium text-sm">Upload, edit and allocate slider media files for the shop client application.</p>
        </div>
      </div>

      {/* UPLOAD FORM CARD */}
      <div className="ui-card ui-card-pad mb-8 bg-white border border-slate-100 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white shadow-lg shadow-emerald-600/20">
            <ImageIcon size={16} />
          </div>
          <h3 className="text-base font-extrabold text-slate-800">Add New Banner / Image Assets</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {/* UPLOAD TYPE */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Target Section</label>
            <select
              value={uploadType}
              onChange={(e) => {
                const next = e.target.value;
                setUploadType(next);
                setTitle(next === 'home' ? 'home' : next === 'who' ? 'who-we-are' : '');
                setCategoryName('');
              }}
              className="ui-input w-full text-xs bg-white cursor-pointer"
            >
              <option value="home">Home Slider Banners</option>
              <option value="who">Who We Are section banner</option>
              <option value="category">Category navigation banners</option>
            </select>
          </div>

          {/* DYNAMIC FIELD (CATEGORY OR TITLE) */}
          <div>
            {uploadType === 'category' ? (
              <>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Category Link</label>
                <select
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  className="ui-input w-full text-xs bg-white cursor-pointer"
                >
                  <option value="">Select linked category</option>
                  {categories.map((c) => (
                    <option key={c.id ?? c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Banner Slug Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="ui-input w-full text-xs"
                  placeholder={uploadType === 'home' ? 'home' : 'who-we-are'}
                />
              </>
            )}
          </div>

          {/* FILE UPLOAD FIELD */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Select Media File</label>
            <label className="flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/10 transition-colors">
              <Upload className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-bold text-slate-700">Choose Images</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
            {files.length > 0 && (
              <p className="mt-2 text-[11px] text-slate-400 font-bold">{files.length} image(s) queued</p>
            )}
          </div>
        </div>

        {/* SELECTED QUEUED FILES LIST */}
        {files.length > 0 && (
          <div className="mt-6 pt-5 border-t border-slate-100 animate-fade-in">
            <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">Images Queue (Ready to Save)</p>
            <div className="flex flex-wrap gap-4">
              {files.map((file, idx) => (
                <div
                  key={`${file.name}-${idx}`}
                  className="relative h-20 w-36 rounded-xl overflow-hidden border border-slate-200 bg-slate-50/50 shadow-sm animate-fade-in group"
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeSelected(idx)}
                    className="absolute top-1.5 right-1.5 bg-rose-500/80 hover:bg-rose-600 text-white rounded-full w-5 h-5 flex items-center justify-center font-bold text-xs shadow-md transition-colors"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <p className="mt-4 p-3 bg-rose-50 border border-rose-100 rounded-xl text-xs text-rose-700 font-semibold animate-fade-in">
            ⚠️ {error}
          </p>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="ui-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Upload size={15} />
            {saving ? 'Uploading files...' : 'Save Banners'}
          </button>
        </div>
      </div>

      {/* FILTER & GRID CONTAINER */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h3 className="text-base font-extrabold text-slate-800 flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
          Active Display Assets ({filteredBanners.length})
        </h3>
        
        {/* VIEW FILTER TABS */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl self-start sm:self-auto">
          {[
            { k: 'home', l: 'Home Slider' },
            { k: 'who', l: 'Who We Are' },
            { k: 'category', l: 'Category' },
            { k: 'all', l: 'All Assets' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setViewFilter(t.k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                viewFilter === t.k
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {/* RENDER BANNERS IN VISUAL CARDS GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {filteredBanners.map((b) => (
          <div key={b.id} className="ui-card overflow-hidden border border-slate-100 bg-white shadow-sm flex flex-col justify-between group hover:border-emerald-200/50 hover:shadow-md transition-all duration-300">
            {/* Banner Media Block */}
            <div className="relative aspect-[16/9] w-full bg-slate-50 overflow-hidden border-b border-slate-100">
              {b.imagePath ? (
                <img
                  src={getImageUrl(b.imagePath)}
                  alt={b.title || 'Banner'}
                  className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                />
              ) : (
                <div className="h-full w-full flex flex-col items-center justify-center text-slate-300 text-xs">
                  <ImageIcon size={24} className="mb-1.5 text-slate-300" />
                  No Image Available
                </div>
              )}
              {/* Overlay pill */}
              <div className="absolute top-2.5 left-2.5">
                <span className="inline-flex px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase bg-slate-900/75 backdrop-blur-[2px] text-white tracking-wider">
                  {b.section}
                </span>
              </div>
              
              {/* Active/Inactive Status indicator */}
              <div className="absolute top-2.5 right-2.5">
                <span className={`inline-flex px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase border ${
                  b.isActive 
                    ? 'bg-emerald-500/90 text-white border-transparent'
                    : 'bg-slate-900/60 text-slate-200 border-transparent'
                }`}>
                  {b.isActive ? 'Active' : 'Hidden'}
                </span>
              </div>
            </div>

            {/* Banner Specs Info */}
            <div className="p-4 flex-1 flex flex-col justify-between bg-white">
              <div>
                <h4 className="font-extrabold text-slate-800 text-sm truncate">{b.title || 'Unnamed Asset'}</h4>
                {b.section === 'category' && (
                  <p className="text-[10px] text-blue-600 font-bold bg-blue-50 border border-blue-100 px-2 py-0.5 rounded mt-1.5 inline-block">
                    Category: {b.categoryName || '—'}
                  </p>
                )}
              </div>

              {/* CARD ACTIONS TOOLBAR */}
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-1">
                  {b.imagePath && (
                    <a
                      href={getImageUrl(b.imagePath)}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-slate-50 rounded-lg transition-colors"
                      title="View Full Resolution Image"
                    >
                      <Eye size={15} />
                    </a>
                  )}
                  <button
                    onClick={() => openEdit(b)}
                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-slate-50 rounded-lg transition-colors"
                    title="Edit Metadata"
                  >
                    <Edit size={14} />
                  </button>
                </div>
                
                <button
                  onClick={() => handleDelete(b.id)}
                  className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-slate-50 rounded-lg transition-colors"
                  title="Remove Asset"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}

        {filteredBanners.length === 0 && (
          <div className="col-span-full py-16 text-center ui-card border border-slate-150 bg-white">
            <p className="text-slate-400 font-semibold text-sm">No display banners registered in this category.</p>
          </div>
        )}
      </div>

      {/* EDIT MODAL OVERLAY */}
      {editing && (
        <div className="ui-modal-overlay">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden animate-fade-in border border-slate-100">
            {/* Header */}
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-emerald-50/60 to-green-50/60">
              <div>
                <h3 className="text-base font-extrabold text-slate-800">Edit Media Settings</h3>
                <p className="text-[11px] text-slate-400 font-medium mt-0.5">Update layout links, sections or image files</p>
              </div>
              <button
                onClick={closeEdit}
                className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors border border-slate-200 bg-white"
              >
                <X size={15} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Banner Title</label>
                  <input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="ui-input w-full text-xs"
                    placeholder="Banner title"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Display Section</label>
                  <select
                    value={editSection}
                    onChange={(e) => {
                      const next = e.target.value;
                      setEditSection(next);
                      if (next !== 'category') setEditCategoryName('');
                    }}
                    className="ui-input w-full text-xs bg-white cursor-pointer"
                  >
                    <option value="banner">Slider banner</option>
                    <option value="category">Category navigator banner</option>
                  </select>
                </div>
              </div>

              {editSection === 'category' && (
                <div className="animate-fade-in">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Category Name</label>
                  <select
                    value={editCategoryName}
                    onChange={(e) => setEditCategoryName(e.target.value)}
                    className="ui-input w-full text-xs bg-white cursor-pointer"
                  >
                    <option value="">Select linked category</option>
                    {categories.map((c) => (
                      <option key={c.id ?? c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Availability Status</label>
                <select
                  value={editIsActive ? 'true' : 'false'}
                  onChange={(e) => setEditIsActive(e.target.value === 'true')}
                  className="ui-input w-full text-xs bg-white cursor-pointer"
                >
                  <option value="true">Active (Displaying on customer site)</option>
                  <option value="false">Inactive (Hidden from customers)</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4 items-start pt-2">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Current Media</label>
                  {editing.imagePath ? (
                    <img
                      src={getImageUrl(editing.imagePath)}
                      alt={editing.title || 'Banner'}
                      className="h-24 w-full object-cover rounded-xl border border-slate-150"
                    />
                  ) : (
                    <div className="h-24 w-full flex items-center justify-center rounded-xl bg-slate-50 text-slate-300 text-xs">
                      No Image File
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Replace File</label>
                  <label className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/10 transition-colors">
                    <Upload className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-bold text-slate-700">New Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        setEditFile(f || null);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {editFile && (
                    <div className="mt-2.5 animate-fade-in">
                      <img
                        src={URL.createObjectURL(editFile)}
                        alt="New banner"
                        className="h-20 w-full object-cover rounded-xl border border-slate-150"
                      />
                      <button
                        type="button"
                        onClick={() => setEditFile(null)}
                        className="mt-1.5 text-[10px] font-bold text-rose-600 hover:text-rose-700 block"
                      >
                        ✕ Cancel selection
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2.5 pt-4 border-t border-slate-100 mt-6 bg-slate-50 -mx-6 -mb-6 p-4">
                <button onClick={closeEdit} className="ui-btn-secondary !px-4 !py-2 !rounded-xl text-xs">
                  Cancel
                </button>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="ui-btn-primary !px-4 !py-2 !rounded-xl text-xs"
                >
                  {saving ? 'Saving changes...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Banners;
